import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Sparkles, Search, X } from "lucide-react";

const PLACEHOLDER = /\[\[(.+?)\]\]/;

/**
 * Canned text (macro) support for a textarea: `.trigger` + Tab expands,
 * Tab again jumps to the next [[placeholder]].
 */
export function useCannedText(field, value, onChange, { disabled = false } = {}) {
  const ref = useRef(null);
  const [snippets, setSnippets] = useState([]);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");

  useEffect(() => {
    if (disabled) return;
    api.get("/canned-texts", { params: { field } })
      .then((r) => setSnippets(r.data)).catch(() => setSnippets([]));
  }, [field, disabled]);

  const byTrigger = useMemo(() => {
    const map = {};
    snippets.forEach((s) => { map[s.trigger] = s; });
    return map;
  }, [snippets]);

  const focusPlaceholder = useCallback((text, from) => {
    const rest = text.slice(from);
    const hit = rest.match(PLACEHOLDER);
    const el = ref.current;
    if (!el) return;
    requestAnimationFrame(() => {
      el.focus();
      if (hit) {
        const start = from + hit.index;
        el.setSelectionRange(start, start + hit[0].length);
      } else {
        el.setSelectionRange(from, from);
      }
    });
  }, []);

  const insert = useCallback((snippet) => {
    const el = ref.current;
    const text = value || "";
    const at = el ? el.selectionStart : text.length;
    const before = text.slice(0, at);
    const after = text.slice(el ? el.selectionEnd : text.length);
    const glue = before && !before.endsWith("\n") && !before.endsWith(" ") ? "\n" : "";
    const next = `${before}${glue}${snippet.body}${after}`;
    onChange(next);
    api.post(`/canned-texts/${snippet.id}/use`).catch(() => {});
    setOpen(false);
    focusPlaceholder(next, before.length + glue.length);
  }, [value, onChange, focusPlaceholder]);

  const handleKeyDown = useCallback((e) => {
    if (e.key !== "Tab" || e.shiftKey || disabled) return;
    const el = ref.current;
    if (!el) return;
    const text = value || "";
    const caret = el.selectionStart;
    const word = (text.slice(0, caret).match(/\.([a-z0-9_-]{2,20})$/i) || [])[1];
    if (word && byTrigger[word.toLowerCase()]) {
      e.preventDefault();
      const snippet = byTrigger[word.toLowerCase()];
      const start = caret - word.length - 1;
      const next = text.slice(0, start) + snippet.body + text.slice(caret);
      onChange(next);
      api.post(`/canned-texts/${snippet.id}/use`).catch(() => {});
      toast.success(`Inserted “${snippet.title}”`);
      focusPlaceholder(next, start);
      return;
    }
    const rest = text.slice(caret);
    const hit = rest.match(PLACEHOLDER);
    if (hit) {
      e.preventDefault();
      const start = caret + hit.index;
      el.setSelectionRange(start, start + hit[0].length);
    }
  }, [value, onChange, byTrigger, focusPlaceholder, disabled]);

  const filtered = q
    ? snippets.filter((s) => `${s.trigger} ${s.title} ${s.body}`.toLowerCase().includes(q.toLowerCase()))
    : snippets;

  const trigger = disabled ? null : (
    <button type="button" onClick={() => setOpen((o) => !o)} data-testid={`canned-open-${field}`}
            className="inline-flex h-8 items-center gap-1 border border-slate-300 px-2 text-xs font-semibold text-slate-700 hover:border-[#134e4a] hover:text-[#134e4a]">
      <Sparkles size={13} /> Canned text
    </button>
  );

  const panel = disabled || !open ? null : (
    <div className="mt-2 border border-slate-300 bg-white shadow-sm" data-testid={`canned-panel-${field}`}>
      <div className="flex items-center gap-2 border-b border-slate-200 bg-slate-50 px-2 py-1.5">
        <Search size={14} className="text-slate-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} data-testid={`canned-search-${field}`}
               placeholder="Search snippets or type the trigger" className="h-8 border-0 bg-transparent px-0 text-sm shadow-none focus-visible:ring-0" />
        <button type="button" onClick={() => setOpen(false)} data-testid={`canned-close-${field}`}
                className="text-slate-400 hover:text-slate-700"><X size={15} /></button>
      </div>
      <ul className="max-h-64 divide-y divide-slate-100 overflow-y-auto">
        {filtered.length === 0 && (
          <li className="px-3 py-4 text-sm text-slate-500">
            No snippets yet. Build the library in Canned Texts.
          </li>
        )}
        {filtered.map((s) => (
          <li key={s.id}>
            <button type="button" onClick={() => insert(s)} data-testid={`canned-insert-${s.trigger}`}
                    className="w-full px-3 py-2 text-left hover:bg-teal-50">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-mono text-xs font-bold text-[#134e4a]">.{s.trigger}</span>
                <span className="text-sm font-semibold text-slate-800">{s.title}</span>
                {s.scope === "private" && (
                  <span className="bg-slate-100 px-1.5 text-[10px] font-bold uppercase text-slate-600">mine</span>
                )}
              </div>
              <div className="mt-0.5 line-clamp-2 whitespace-pre-wrap text-xs text-slate-500">{s.body}</div>
            </button>
          </li>
        ))}
      </ul>
      <p className="border-t border-slate-200 bg-slate-50 px-3 py-1.5 text-[11px] text-slate-500">
        Tip: type <span className="font-mono font-bold">.trigger</span> then Tab to expand. Tab again jumps to the next blank.
      </p>
    </div>
  );

  return { ref, trigger, panel, handleKeyDown, snippets };
}
